import { motion } from "framer-motion";
import "@/App.css";
import { Heart } from "lucide-react";

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 1.2, ease: "easeOut" }
  }
};

const fadeIn = {
  hidden: { opacity: 0 },
  visible: { 
    opacity: 1,
    transition: { duration: 1.5, ease: "easeOut" }
  }
};

const floatingAnimation = {
  y: [0, -15, 0],
  transition: {
    duration: 6,
    repeat: Infinity,
    ease: "easeInOut"
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.3,
      delayChildren: 0.2
    }
  }
};

function App() {
  return (
    <div className="wedding-page" data-testid="wedding-page">
      {/* Grain texture overlay */}
      <div className="grain-overlay" />
      
      {/* Ambient glow orbs */}
      <div className="glow-orb glow-orb-1" />
      <div className="glow-orb glow-orb-2" />
      <div className="glow-orb glow-orb-3" />

      {/* Main content */}
      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-6 md:px-12 lg:px-24 py-16">
        
        {/* Hero Section */}
        <motion.div 
          className="text-center max-w-4xl mx-auto"
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
        >
          {/* Overline */}
          <motion.p 
            className="font-manrope text-xs tracking-[0.25em] uppercase text-[#DCAE96] font-medium mb-6"
            variants={fadeInUp}
            data-testid="overline-text"
          >
            We're Getting Married
          </motion.p>

          {/* Decorative line */}
          <motion.div 
            className="decorative-line mx-auto mb-8"
            variants={fadeIn}
          />

          {/* Main heading */}
          <motion.h1 
            className="font-cormorant text-5xl sm:text-6xl lg:text-7xl font-light text-[#2C181D] tracking-tight leading-none mb-6"
            variants={fadeInUp}
            data-testid="main-heading"
          >
            Save the Date
          </motion.h1>

          {/* Date */}
          <motion.p
            className="font-manrope text-xl md:text-3xl text-[#5C3D46] font-italic leading-relaxed max-w-md mx-auto mb-10"
            variants={fadeInUp}
            data-testid="save-the-date-when"
          >
            29th January 2027
          </motion.p>

          {/* Subheading */}
          <motion.p 
            className="font-manrope text-base md:text-lg text-[#5C3D46] font-light leading-relaxed max-w-md mx-auto mb-12"
            variants={fadeInUp}
            data-testid="subheading-text"
          >
            Something beautiful is on the way. Our love story continues with a celebration you won't want to miss.
          </motion.p>

          {/* Arched hero image */}
          <motion.div 
            className="arched-image w-64 h-80 sm:w-80 sm:h-96 lg:w-96 lg:h-[450px] mx-auto mb-12 shadow-2xl"
            variants={fadeInUp}
            data-testid="hero-image-container"
          >
            <img 
              src="https://images.unsplash.com/photo-1658851866325-49fb8b7fbcb2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwxfHxyb21hbnRpYyUyMGNvdXBsZSUyMHN1bnNldHxlbnwwfHx8fDE3NzQ4NTg2NzN8MA&ixlib=rb-4.1.0&q=85&w=800"
              alt="Romantic couple at sunset"
              className="w-full h-full object-cover"
              data-testid="hero-image"
            />
          </motion.div>

          {/* Coming Soon badge */}
          <motion.div 
            className="glass-overlay inline-flex items-center gap-2 px-6 py-3 rounded-full mb-8"
            variants={fadeInUp}
            data-testid="coming-soon-badge"
          >
            <Heart className="w-4 h-4 text-[#DCAE96]" />
            <span className="font-manrope text-sm text-[#2C181D] font-medium tracking-wide">
              Coming Soon
            </span>
            <Heart className="w-4 h-4 text-[#DCAE96]" />
          </motion.div>

          {/* Footer text */}
          <motion.p 
            className="font-cormorant text-2xl sm:text-3xl italic text-[#5C3D46] font-light"
            variants={fadeInUp}
            data-testid="footer-text"
          >
            Stay tuned for more details
          </motion.p>
        </motion.div>

        {/* Floating decorative images */}
        <motion.div 
          className="absolute top-20 left-8 lg:left-24 hidden lg:block"
          animate={floatingAnimation}
        >
          <div className="floating-image pill w-20 h-32 lg:w-24 lg:h-40 opacity-80" data-testid="floating-image-1">
            <img 
              src="https://images.unsplash.com/photo-1689614466445-7dfca8286992?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwyfHxzb2Z0JTIwcGluayUyMHdlZGRpbmclMjBmbG93ZXJzfGVufDB8fHx8MTc3NDg1ODY3NHww&ixlib=rb-4.1.0&q=85&w=200"
              alt="Soft pink flowers"
              className="w-full h-full object-cover"
            />
          </div>
        </motion.div>

        <motion.div 
          className="absolute bottom-32 right-8 lg:right-24 hidden lg:block"
          animate={{
            ...floatingAnimation,
            transition: { ...floatingAnimation.transition, delay: 1 }
          }}
        >
          <div className="floating-image w-28 h-36 lg:w-32 lg:h-44 opacity-80" data-testid="floating-image-2">
            <img 
              src="https://images.pexels.com/photos/10772718/pexels-photo-10772718.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              alt="Elegant pink rose bouquet"
              className="w-full h-full object-cover"
            />
          </div>
        </motion.div>

        {/* Additional floating element */}
        <motion.div 
          className="absolute top-1/3 right-12 lg:right-32 hidden lg:block"
          animate={{
            ...floatingAnimation,
            transition: { ...floatingAnimation.transition, delay: 2 }
          }}
        >
          <div className="w-16 h-16 rounded-full bg-[#DCAE96]/20 backdrop-blur-sm border border-[#DCAE96]/30 flex items-center justify-center" data-testid="decorative-circle">
            <Heart className="w-6 h-6 text-[#DCAE96]" />
          </div>
        </motion.div>

        {/* Bottom left decorative element */}
        <motion.div 
          className="absolute bottom-20 left-12 lg:left-32 hidden lg:block"
          animate={{
            ...floatingAnimation,
            transition: { ...floatingAnimation.transition, delay: 3 }
          }}
        >
          <div className="w-12 h-12 rounded-full bg-[#FFF0F5] border border-[#E8D5D0] flex items-center justify-center" data-testid="decorative-circle-2">
            <div className="w-2 h-2 rounded-full bg-[#DCAE96]" />
          </div>
        </motion.div>
      </div>

      {/* Background abstract texture (subtle) */}
      <div 
        className="absolute inset-0 opacity-[0.03] mix-blend-multiply pointer-events-none"
        style={{
          backgroundImage: `url("https://images.unsplash.com/photo-1770954098628-182216af4d85?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMHNvZnQlMjBwaW5rJTIwcGFzdGVsJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3NzQ4NTg2ODl8MA&ixlib=rb-4.1.0&q=85")`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
        data-testid="background-texture"
      />
    </div>
  );
}

export default App;
